/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { yupResolver } from '@hookform/resolvers/yup';
import { Edit } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { TextareaAutosize, FormHelperText, Stack, Divider, Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { FormProvider, useForm, FieldValues, Controller } from 'react-hook-form';
import {
  IJob,
  ICandidate,
  getJobByJobCode,
  JobAssignmentStatusFilter,
  getJobsFromAdvancedQuery,
  ICandidateResume,
  uploadFileToS3,
  newMappingEntity,
  IMappingFormSettingsResponse,
} from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  Item,
  SalaryDetails,
  JobPreference,
  IConversationState,
  WorkExpForm,
  ControlledLanguageInput,
  WsButton,
  ControlledRecruiterActivity,
  LocationInfo,
  ChatBoxHeader,
  Label,
  WsFileUpload,
  WsColors,
  AssessmentInfo,
  DocumentInfo,
  PersonalInfo,
} from 'wisestep-base';

import { isAnyFieldShown } from '../../../features/crmSettings/helper/configMappingFormHelper';
import { warning, success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { mappingFormSchema } from '../schema/formSchema';
import IOtherDocument from '../types/IOtherDocument';
import { ITrackCvPages, downloadCV, trackCV } from '../utils/cvHelper';
import { submissionFormDefaultValues } from '../utils/fieldDefaultValues';
import { getMappingFailedNotification, saveMapping, updateMapping } from '../utils/saveMapping';

import ActionFooter from './ActionFooter';
import CVViewModal from './CVViewModal';
import WidgetJobManagement from './WidgetJobManagement';

interface IMappingForm {
  conversation?: ICandidate;
  onClose?: () => void;
  state: IConversationState;
  recruiterAction: string | null;
  selectedJob?: IJob | null;
  closeDrawer?: (args?: ICandidate) => void;
  mappingFormSettings?: IMappingFormSettingsResponse;
}

interface IMappingFormState {
  resumeURl: string | null;
  openCvView: boolean;
  candidateInfo: ICandidate | null;
  candidateResumes: ICandidateResume[];
  disableSubmit: boolean;
  results: string[];
  actions: string[];
  jobForUpdate: IJob | null;
}
interface IProfileInfoAccordion {
  header: string;
  children: ReactNode;
  isExpand?: boolean;
}

const ProfileInfoAccordion: FC<IProfileInfoAccordion> = ({
  header,
  children,
  isExpand = false,
}) => {
  const [expand, setExpand] = useState<boolean>(isExpand);
  useEffect(() => setExpand(isExpand), [isExpand]);
  return (
    <>
      <Grid container item xs={12}>
        <Item sx={{ width: '100%' }}>
          <WsTypography
            display="inline"
            variant={WsTypoVariants.SUB_TITLE}
            color={WsTypoColors.PRIMARY}
          >
            {header}
          </WsTypography>
          {!expand && (
            <Edit
              onClick={() => setExpand(true)}
              sx={{ float: 'right', cursor: 'pointer', color: WsColors.TheBlues500 }}
            />
          )}
          {expand && (
            <ExpandLessIcon
              onClick={() => setExpand(false)}
              sx={{ float: 'right', cursor: 'pointer', color: WsColors.TheBlues500 }}
            />
          )}
          <Divider sx={{ mt: 1 }} />
        </Item>
      </Grid>
      {expand && children}
    </>
  );
};
const MappingForm: FC<IMappingForm> = ({
  state,
  recruiterAction,
  selectedJob,
  mappingFormSettings,
  conversation,
  onClose,
}) => {
  const refSubmitButtom = useRef<HTMLButtonElement>(null);
  const activityConversation = useConversationWidgetStore((state) => state.activityConversation);
  const activityConversationFor = useConversationWidgetStore(
    (state) => state.activityConversationFor
  );
  const [formState, setFormState] = useState<IMappingFormState>({
    resumeURl: null,
    openCvView: false,
    candidateInfo: activityConversation ?? state.candidate,
    candidateResumes: [],
    disableSubmit: false,
    results: [],
    actions: [],
    jobForUpdate: null,
  });

  const onCandidateUpdate = useConversationWidgetStore((state) => state.onCandidateUpdate);
  const footerAction = useConversationWidgetStore((state) => state.footerAction);
  const setTempConv = useConversationWidgetStore((state) => state.setTempConv);
  const updateFromPage = useConversationWidgetStore((state) => state.updateFromPage);
  const mapBeforeNavigation = useConversationWidgetStore((state) => state.mapBeforeNavigation);
  const candidate = activityConversation ?? state.candidate;
  const contactActionObject = !isEmpty(candidate?.contactActionsObjs)
    ? candidate?.contactActionsObjs
    : conversation?.contactActionsObjs;
  const contactActions =
    contactActionObject && contactActionObject[contactActionObject.length - 1].actions;
  const contactResults =
    contactActionObject && contactActionObject[contactActionObject.length - 1].result.result;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const disableMapping = !activityConversation
    ? candidate?.jobId &&
      candidate.jobId === selectedJob?.id &&
      candidate?.isJobCandidateMapping === false
    : false;
  const formOptions = {
    resolver: yupResolver(
      mappingFormSchema(mappingFormSettings, candidate?.profileURLs?.[0] ?? '')
    ),
    defaultValues: submissionFormDefaultValues(candidate, selectedJob),
  };
  const setIsMappingFormChanged = useConversationWidgetStore(
    (state) => state.setIsMappingFormChanged
  );
  const isMappingFormChanged = useConversationWidgetStore((state) => state.isMappingFormChanged);
  const [otherDocument, setOtherDocument] = useState<IOtherDocument[]>([]);
  let showWorkExpSettings = true;
  let showJobPreferenceSettings = true;
  let showSalaryInfoSettings = true;
  let showAssessmentInfoSettings = true;

  if (mappingFormSettings) {
    const showTitlesObj = isAnyFieldShown(mappingFormSettings);
    ({
      showWorkExpSettings,
      showJobPreferenceSettings,
      showSalaryInfoSettings,
      showAssessmentInfoSettings,
    } = showTitlesObj);
  }
  const showCandidatePrefTitle =
    mappingFormSettings?.locations.show ||
    showWorkExpSettings ||
    showJobPreferenceSettings ||
    showSalaryInfoSettings ||
    showAssessmentInfoSettings ||
    mappingFormSettings?.otherDocuments.show ||
    mappingFormSettings?.languages.show ||
    mappingFormSettings?.resume.show;

  useEffect(() => {
    contactActions && setFormState((m) => ({ ...m, actions: contactActions }));
    contactResults && setFormState((m) => ({ ...m, results: contactResults as string[] }));
  }, [contactActions, contactResults]);

  const methods = useForm(formOptions);

  const {
    handleSubmit,
    watch,
    control,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    const jobCode = activityConversation?.jobCode;
    if (jobCode) {
      (async () => {
        const response = await getJobsFromAdvancedQuery({
          jobCodes: [jobCode],
          page: 0,
          size: 2,
          sort: ['scoringData.score,desc'],
          searchAcross: ['MY_TEAMS', 'MY_PERSONAL_DATA'],
          jobAssignmentStatusFilter: JobAssignmentStatusFilter.Everything,
          jobStatus: 'All',
        });

        if (!isEmpty(response.data) && response.data[0])
          setFormState((m) => ({ ...m, jobForUpdate: response.data[0] }));

        if (isEmpty(response.data)) {
          try {
            const jobResponse = await getJobByJobCode(jobCode);
            setFormState((m) => ({ ...m, jobForUpdate: jobResponse as IJob }));
          } catch (e) {
            console.log(e);
          }
        }
      })();
    }
  }, [activityConversation]);

  useEffect(() => {
    if (isMappingFormChanged == false && isDirty) setIsMappingFormChanged(true);
  }, [isDirty, isMappingFormChanged, setIsMappingFormChanged]);

  const [expand, setExpand] = useState<boolean>(false);
  const onError = () => {
    setExpand(true);
    setNotification(warning('Please enter required fields'));
  };
  const updateMap = useCallback(
    async (data: FieldValues) => {
      const response =
        formState.candidateInfo && (await updateMapping(data, formState.candidateInfo));
      if (response && response?.status != 'fail') {
        setNotification(success('Mapping Updated'));
        updateFromPage('mapping');
        setIsMappingFormChanged(false);
        mapBeforeNavigation ? setTempConv(response) : onCandidateUpdate(response);
      } else {
        setFormState((m) => ({ ...m, disableSubmit: false }));
        setNotification(failed(getMappingFailedNotification(response?.response)));
      }
    },
    [
      formState.candidateInfo,
      mapBeforeNavigation,
      onCandidateUpdate,
      setIsMappingFormChanged,
      setNotification,
      setTempConv,
      updateFromPage,
    ]
  );

  const onFormSubmit = useCallback(
    async (data: FieldValues) => {
      data.downloadSource = candidate.downloadSource ?? 'NOT_SPECIFIED';
      data.source = watch('source');
      data.willingToTravel = watch('willingToTravel');
      data.preferredLocation = watch('preferredLocation');
      data.relocationPreference = watch('relocationList');
      data.authorizationCategory = watch('authorizationCategory');

      setFormState((m) => ({ ...m, disableSubmit: true }));
      if (activityConversation && formState.candidateInfo) {
        updateMap(data);
      } else if (formState.candidateInfo && selectedJob && selectedJob.id) {
        const response = await saveMapping(data, formState.candidateInfo, selectedJob);
        if (response && response.status == 'success') {
          setNotification(success('Candidate is mapped to selected job'));
          updateFromPage('mapping');
          setIsMappingFormChanged(false);
          mapBeforeNavigation
            ? setTempConv(response.response)
            : onCandidateUpdate(response.response);
        } else {
          response?.response.indexOf('There is no accepted work assignment for the job') > -1
            ? setNotification(
                failed(
                  'This assigned job is not been accepted by the recruiter. Please accept the job then map the candidate.'
                )
              )
            : setNotification(failed('Mapping failed'));
          setFormState((m) => ({ ...m, disableSubmit: false }));
        }
      } else if (isEmpty(recruiterAction)) {
        updateMap(data);
      } else if (isEmpty(selectedJob)) {
        setFormState((m) => ({ ...m, disableSubmit: false }));
        setNotification(warning('Please select job first'));
      }
    },
    [
      activityConversation,
      candidate.downloadSource,
      formState.candidateInfo,
      mapBeforeNavigation,
      onCandidateUpdate,
      recruiterAction,
      selectedJob,
      setIsMappingFormChanged,
      setNotification,
      setTempConv,
      updateFromPage,
      updateMap,
      watch,
    ]
  );

  useEffect(() => {
    if (mapBeforeNavigation) {
      refSubmitButtom?.current?.click();
    }
  }, [handleSubmit, mapBeforeNavigation, onFormSubmit]);

  const onUpload = async (file: File[], field: 'resume' | 'otherDocuments') => {
    const url = await uploadFileToS3(file[0]);
    if (field === 'resume') {
      const alteredCandidate = formState.candidateInfo ?? newMappingEntity();

      const oldResumes = alteredCandidate?.mappedCandidateResumes
        ? alteredCandidate?.mappedCandidateResumes
        : [];
      const alteredResumes = [...oldResumes, ...formState.candidateResumes];
      alteredResumes.push({
        candidateMappingId: '',
        email: '',
        gitHubLink: '',
        id: '',
        linkedInLink: '',
        phone: '',
        pinCode: '',
        resumeFileOriginalName: file[0].name,
        resumeLocation: url,
        resumeUploadedOn: new Date(),
      });

      alteredCandidate.resumeLocation = url;
      alteredCandidate.resumeFileOriginalName = file[0].name;
      alteredCandidate.resumeUploadedOn = new Date();
      alteredCandidate.mappedCandidateResumes = alteredResumes;
      setFormState((m) => ({ ...m, candidateInfo: { ...alteredCandidate } }));
    }
    if (field === 'otherDocuments') {
      const alteredCandidate = formState.candidateInfo ?? newMappingEntity();
      const alteredDocuments = [...otherDocument];

      alteredDocuments.push({
        documentLocation: url,
        originalName: file[0].name,
        tag: null,
        uploadedOn: new Date(),
      });
      setOtherDocument([...alteredDocuments]);
      alteredCandidate.otherDocuments = alteredDocuments;
      setFormState((m) => ({ ...m, candidateInfo: { ...alteredCandidate } }));
    }
  };

  const resumeAction = (action: string, url: string | null) => {
    switch (action) {
      case 'view':
        if (url) {
          trackCV('view', url, ITrackCvPages.PIPELINE);
          setFormState((m) => ({ ...m, resumeURl: url }));
        }
        setFormState((m) => ({ ...m, openCvView: true }));
        break;
      case 'download':
        if (!isEmpty(url)) {
          trackCV('download', url, ITrackCvPages.PIPELINE);
          url && downloadCV(url);
        }
        break;
      case 'removeCandidate':
        if (formState.candidateInfo) {
          const alteredCandidate = { ...formState.candidateInfo };
          if (alteredCandidate?.mappedCandidateResumes) {
            const index = alteredCandidate?.mappedCandidateResumes?.findIndex(
              (resume) => resume.resumeLocation === url
            );
            if (index != -1) {
              alteredCandidate?.mappedCandidateResumes?.splice(index, 1);
              setFormState((m) => ({
                ...m,
                candidateResumes: alteredCandidate?.mappedCandidateResumes as ICandidateResume[],
              }));
            }
            setFormState((m) => ({ ...m, candidateInfo: alteredCandidate }));
          }
        }
        break;
      case 'removeOtherDocument':
        if (candidate) {
          const alteredCandidate = { ...candidate };
          alteredCandidate.otherDocuments = alteredCandidate?.otherDocuments?.filter(
            (document) => document.documentLocation != url
          );
          setOtherDocument((prev) => {
            const newDocArray = prev.filter((prevDoc) => prevDoc.documentLocation != url);
            alteredCandidate.otherDocuments = newDocArray;
            return newDocArray;
          });
          setFormState((m) => ({ ...m, candidateInfo: alteredCandidate }));
        }
        break;
    }
  };

  return (
    <FormProvider {...methods}>
      {!disableMapping && activityConversationFor !== 'submission' ? (
        <form
          onSubmit={handleSubmit(onFormSubmit, onError)}
          onKeyDown={(e) => e.code === 'Enter' && e.preventDefault()}
        >
          <Grid item xs={12}>
            <ChatBoxHeader>
              <Grid container alignItems="center">
                <Grid item xs={9}>
                  <WsTypography
                    variant={WsTypoVariants.TITLE}
                    color={WsTypoColors.PRIMARY}
                    bold={true}
                  >
                    Map Conversation
                  </WsTypography>
                </Grid>
                <Grid item xs={3} display="flex" justifyContent="flex-end">
                  <WidgetJobManagement
                    selectedJob={
                      activityConversation
                        ? formState.jobForUpdate
                          ? formState.jobForUpdate
                          : null
                        : selectedJob
                    }
                    jobFetchFailure={
                      !isEmpty(activityConversation) && isEmpty(formState.jobForUpdate)
                    }
                    ss={12}
                    hideRemove={true}
                  />
                  {conversation && <CloseIcon onClick={onClose} sx={{ cursor: 'pointer' }} />}
                </Grid>
              </Grid>
            </ChatBoxHeader>
          </Grid>
          <Grid item xs={12} sx={{ maxHeight: '82vh', overflowY: 'auto' }}>
            <ProfileInfoAccordion header="Personal Info" isExpand={expand}>
              <PersonalInfo
                candidate={candidate}
                purpose="mapping"
                mappingFormSettings={mappingFormSettings}
                showTitle={false}
              />
            </ProfileInfoAccordion>
            <Item>
              {mappingFormSettings?.recruiterActivity && (
                <ControlledRecruiterActivity
                  name="recruiterActivity"
                  candidateInteractionMessagingPipeline={
                    state.messageAndInteractionPipeline.candidateInteractionMessagingPipeline
                  }
                  selectedActions={
                    isEmpty(activityConversation) && recruiterAction && footerAction !== 'Not Sure'
                      ? ['CV Review', recruiterAction]
                      : !isEmpty(activityConversation)
                      ? formState.actions
                      : formState.actions
                      ? formState.actions
                      : []
                  }
                  selectedResults={
                    isEmpty(activityConversation) && footerAction === 'Not Sure'
                      ? ['Not Sure']
                      : !isEmpty(activityConversation)
                      ? formState.results
                      : formState.results
                      ? formState.results
                      : []
                  }
                  candidateInteractionResultPipeline={
                    state.messageAndInteractionPipeline.candidateInteractionResultPipeline
                  }
                />
              )}
              {mappingFormSettings?.notes && (
                <Controller
                  name="notes"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <TextareaAutosize
                        aria-label="notes"
                        onChange={onChange}
                        minRows={6}
                        value={value}
                        placeholder="Call/Message Notes"
                        style={{ width: '100%', marginTop: '12px' }}
                      />
                      {error && <FormHelperText error>{error.message}</FormHelperText>}
                    </>
                  )}
                />
              )}
            </Item>
            {showCandidatePrefTitle && (
              <Item>
                <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
                  Candidate Preferences
                </WsTypography>
              </Item>
            )}
            <LocationInfo
              purpose="mapping"
              mappingFormSettings={mappingFormSettings}
              showTitle={mappingFormSettings?.locations.show}
            />
            <WorkExpForm
              purpose="mapping"
              mappingFormSettings={mappingFormSettings}
              showTitle={showWorkExpSettings}
            />
            <JobPreference
              watch={watch}
              purpose="mapping"
              mappingFormSettings={mappingFormSettings}
              showTitle={showJobPreferenceSettings}
            />
            <SalaryDetails
              showTitle={showSalaryInfoSettings}
              purpose="mapping"
              job={selectedJob ?? undefined}
              mappingFormSettings={mappingFormSettings}
            />
            <AssessmentInfo
              showTitle={showAssessmentInfoSettings}
              purpose="mapping"
              mappingFormSettings={mappingFormSettings}
            />
            {mappingFormSettings?.otherDocuments.show && (
              <DocumentInfo
                purpose="mapping"
                onUpload={(file: File[]) => onUpload(file, 'otherDocuments')}
                candidate={candidate}
                resumeAction={resumeAction}
                mappingFormSettings={mappingFormSettings}
              />
            )}
            {mappingFormSettings?.languages.show && (
              <Item>
                <ControlledLanguageInput
                  name="candidateLanguages"
                  initialValue={[{ languageName: '', fluencyLevel: '' }]}
                />
              </Item>
            )}
            {mappingFormSettings?.resume.show && (
              <Item>
                <Label>CV</Label>
                {formState.candidateInfo?.mappedCandidateResumes?.map(
                  (resume: ICandidateResume, index: number) => (
                    <Stack key={index} direction="row" spacing={2} mb={2}>
                      <WsTypography variant={WsTypoVariants.BODY1}>
                        {resume?.resumeFileOriginalName ?? ''}
                      </WsTypography>
                      <DownloadIcon
                        onClick={() =>
                          resumeAction && resumeAction('download', resume?.resumeLocation)
                        }
                        sx={{ color: WsColors.TheBlues500, fontSize: '24px', cursor: 'pointer' }}
                      />
                      <VisibilityIcon
                        onClick={() => resumeAction && resumeAction('view', resume?.resumeLocation)}
                        sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                      />
                      <CloseIcon
                        onClick={() =>
                          resumeAction && resumeAction('removeCandidate', resume?.resumeLocation)
                        }
                        sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                      />
                    </Stack>
                  )
                )}
                <WsFileUpload
                  multiple={false}
                  onUpload={(file: File[]) => onUpload && onUpload(file, 'resume')}
                  name="resume"
                  placeholderText=" "
                  bgRequired={false}
                  buttonText={'Upload File'}
                  maxFileSize={3}
                />
              </Item>
            )}
            <ActionFooter>
              <Grid item container xs={6} justifyContent="flex-end">
                <WsButton type="submit" disabled={formState.disableSubmit}>
                  {activityConversation ? 'Update' : 'Save'}
                </WsButton>
              </Grid>
            </ActionFooter>
            <button hidden={true} ref={refSubmitButtom} type="submit" />
          </Grid>
        </form>
      ) : (
        <Stack direction="column" spacing={2} mt={4} justifyContent="center" alignItems="center">
          <Item>
            <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.ERROR}>
              Duplicate Candidate
            </WsTypography>
            <WsTypography>
              {`This Candidate is already submitted for the job ${
                activityConversationFor === 'submission'
                  ? formState.jobForUpdate?.title
                  : selectedJob?.title
              } 
            (${
              activityConversationFor === 'submission'
                ? formState.jobForUpdate?.jobCode
                : selectedJob?.jobCode
            }). Therefore you cannot map this candidate)`}
            </WsTypography>
          </Item>
        </Stack>
      )}
      {!isEmpty(formState.candidateInfo?.mappedCandidateResumes) && (
        <CVViewModal
          open={formState.openCvView}
          s3url={formState.resumeURl}
          handleClose={() => setFormState((m) => ({ ...m, openCvView: false }))}
        />
      )}
    </FormProvider>
  );
};

export default MappingForm;
